import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  PlayCircleOutline as PlayCircleOutlineIcon
} from '@mui/icons-material';
import { Therapeutic, TherapeuticCreate, TherapeuticUpdate } from '../../../types/therapeutic';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { useAuth } from '../../../hooks/useAuth';
import { useEnvironment } from '../../../hooks/useEnvironment';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import ConfirmationDialog from '../../safety/ConfirmationDialog/ConfirmationDialog';
import ReviewChangesDialog from '../../safety/ReviewChangesDialog/ReviewChangesDialog';
import DiffReviewer from '../../common/DiffReviewer/DiffReviewer';
import { mapErrorToMessage } from '../../../utils/error-mapping';
import { enrichTherapeutic, findClinicalTrials } from '../../../api/therapeutics';

interface TherapeuticFormProps {
  therapeutic?: Therapeutic | null;
  onSave: (data: TherapeuticCreate | TherapeuticUpdate, id?: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onCancel?: () => void;
}

const TherapeuticForm: React.FC<TherapeuticFormProps> = ({
  therapeutic,
  onSave,
  onDelete,
  onCancel
}) => {
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const isViewer = user?.role === 'viewer';
  const isProduction = environment === 'production';

  const [formData, setFormData] = useState<TherapeuticCreate>({
    name: '',
    summary_description: '',
    description: '',
    research_content_url: '',
    company_code_name: '',
    generic_name: '',
    brand_name: '',
    mechanism_of_action: '',
    biological_targets: [],
    route_of_administration: '',
    indication_summary: '',
    summary: '',
    modality_id: '',
    drug_class_id: '',
  });

  const [originalData, setOriginalData] = useState<Therapeutic | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [biologicalTargetInput, setBiologicalTargetInput] = useState('');

  // Enrichment state
  const [enriching, setEnriching] = useState(false);
  const [showDiffReviewer, setShowDiffReviewer] = useState(false);
  const [enrichmentData, setEnrichmentData] = useState<Record<string, any>>({});
  const [findingTrials, setFindingTrials] = useState(false);

  const { errors, validateAll, clearAllErrors } = useFormValidation({
    name: { required: true, minLength: 1 },
    summary_description: {},
    description: {},
    research_content_url: {},
    company_code_name: {},
    generic_name: {},
    brand_name: {},
    mechanism_of_action: {},
    biological_targets: {},
    route_of_administration: {},
    indication_summary: {},
    summary: {},
    modality_id: {},
    drug_class_id: {}
  });

  useEffect(() => {
    if (therapeutic) {
      const data = {
        name: therapeutic.name || '',
        summary_description: therapeutic.summary_description || '',
        description: therapeutic.description || '',
        research_content_url: therapeutic.research_content_url || '',
        company_code_name: therapeutic.company_code_name || '',
        generic_name: therapeutic.generic_name || '',
        brand_name: therapeutic.brand_name || '',
        mechanism_of_action: therapeutic.mechanism_of_action || '',
        biological_targets: therapeutic.biological_targets || [],
        route_of_administration: therapeutic.route_of_administration || '',
        indication_summary: therapeutic.indication_summary || '',
        summary: therapeutic.summary || '',
        modality_id: therapeutic.modality?.id || '',
        drug_class_id: therapeutic.drug_class?.id || '',
      };
      setFormData(data);
      setOriginalData(therapeutic);
    } else {
      setOriginalData(null);
    }
  }, [therapeutic]);

  const handleChange = (field: keyof TherapeuticCreate, value: any) => {
    setFormData((prev: TherapeuticCreate) => ({ ...prev, [field]: value }));
  };

  const handleAddBiologicalTarget = () => {
    if (biologicalTargetInput.trim()) {
      setFormData((prev: TherapeuticCreate) => ({
        ...prev,
        biological_targets: [...(prev.biological_targets || []), biologicalTargetInput.trim()]
      }));
      setBiologicalTargetInput('');
    }
  };

  const handleRemoveBiologicalTarget = (index: number) => {
    setFormData((prev: TherapeuticCreate) => ({
      ...prev,
      biological_targets: (prev.biological_targets || []).filter((_: string, i: number) => i !== index)
    }));
  };

  const handleEnrich = async () => {
    if (!therapeutic?.id) return;

    setEnriching(true);
    setError(null);

    try {
      const result = await enrichTherapeutic(therapeutic.id);

      if (result.error) {
        throw new Error(result.error);
      }

      if (!result.therapeutic) {
        throw new Error("No enriched data returned");
      }

      // Merge with current form data to create the "enriched" state
      const newEnrichmentData = {
        ...formData,
        ...result.therapeutic,
        // Ensure arrays are handled correctly
        biological_targets: result.therapeutic.biological_targets || formData.biological_targets
      };

      setEnrichmentData(newEnrichmentData);
      setShowDiffReviewer(true);

    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to enrich therapeutic'));
    } finally {
      setEnriching(false);
    }
  };

  const handleFindTrials = async () => {
    if (!therapeutic?.id) return;

    setFindingTrials(true);
    setError(null);

    try {
      const result = await findClinicalTrials(therapeutic.id);

      if (result.error) {
        throw new Error(result.error);
      }

      const count = result.persisted_count || 0;
      // We don't use useNotification here because it's not provided in props, 
      // but the parent might handle it. For now just show a simple alert or success message.
      // Actually, TherapeuticForm doesn't have showNotification, but we can set a temporary success message in error state if we want, or just log.
      // Better to use window.alert if needed, or just hope the backend worked.
      setError(`Successfully found and persisted ${count} clinical trials.`);

    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to find clinical trials'));
    } finally {
      setFindingTrials(false);
    }
  };

  const handleApplyEnrichment = (finalData: Record<string, any>) => {
    // Map the accepted changes back to form data
    setFormData(prev => ({
      ...prev,
      ...finalData
    }));
    setShowDiffReviewer(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAll(formData)) {
      return;
    }

    if (isProduction) {
      setShowReviewDialog(true);
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    setLoading(true);
    setError(null);
    clearAllErrors();
    setShowReviewDialog(false);

    try {
      await onSave(formData, therapeutic?.id);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to save therapeutic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!therapeutic?.id || !onDelete) return;

    setLoading(true);
    setError(null);

    try {
      await onDelete(therapeutic.id);
      setShowDeleteConfirm(false);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to delete therapeutic'));
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header with Title and Delete Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          {therapeutic ? 'Edit Therapeutic' : 'New Therapeutic'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {therapeutic && !isViewer && (user?.role === 'editor' || user?.role === 'admin') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={enriching ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              onClick={handleEnrich}
              disabled={enriching || loading}
            >
              {enriching ? 'Enriching...' : 'Enrich with AI'}
            </Button>
          )}
          {therapeutic && !isViewer && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={findingTrials ? <CircularProgress size={20} color="inherit" /> : <PlayCircleOutlineIcon />}
              onClick={handleFindTrials}
              disabled={findingTrials || loading || enriching}
            >
              {findingTrials ? 'Finding...' : 'Find Trials'}
            </Button>
          )}
          {therapeutic && onDelete && !isViewer && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading || enriching || findingTrials}
            >
              Delete Therapeutic
            </Button>
          )}
        </Box>
      </Box>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={isViewer}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID"
                value={therapeutic?.id || ''}
                disabled
                helperText="Unique identifier (auto-generated)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Code Name"
                value={formData.company_code_name}
                onChange={(e) => handleChange('company_code_name', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Generic Name (INN)"
                value={formData.generic_name}
                onChange={(e) => handleChange('generic_name', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Brand Name"
                value={formData.brand_name}
                onChange={(e) => handleChange('brand_name', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Research Content URL"
                value={formData.research_content_url}
                onChange={(e) => handleChange('research_content_url', e.target.value)}
                disabled={isViewer}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Scientific Details Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Scientific Details
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Mechanism of Action"
                value={formData.mechanism_of_action}
                onChange={(e) => handleChange('mechanism_of_action', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Biological Targets
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={biologicalTargetInput}
                  onChange={(e) => setBiologicalTargetInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBiologicalTarget())}
                  placeholder="Enter a biological target..."
                  disabled={isViewer}
                />
                <Button
                  variant="contained"
                  onClick={handleAddBiologicalTarget}
                  disabled={isViewer || !biologicalTargetInput.trim()}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.biological_targets?.map((target, index) => (
                  <Chip
                    key={index}
                    label={target}
                    onDelete={!isViewer ? () => handleRemoveBiologicalTarget(index) : undefined}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Route of Administration"
                value={formData.route_of_administration}
                onChange={(e) => handleChange('route_of_administration', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Modality ID"
                value={formData.modality_id}
                onChange={(e) => handleChange('modality_id', e.target.value)}
                disabled={isViewer}
                placeholder="Technology ID for modality"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Drug Class ID"
                value={formData.drug_class_id}
                onChange={(e) => handleChange('drug_class_id', e.target.value)}
                disabled={isViewer}
                placeholder="Technology ID for drug class"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Description Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Description
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Summary Description"
                value={formData.summary_description}
                onChange={(e) => handleChange('summary_description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Detailed Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Indication Summary"
                value={formData.indication_summary}
                onChange={(e) => handleChange('indication_summary', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Comprehensive Summary"
                value={formData.summary}
                onChange={(e) => handleChange('summary', e.target.value)}
                disabled={isViewer}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Form Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          {onCancel && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              size="large"
            >
              Cancel
            </Button>
          )}
          {!isViewer && (
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Box>
      </form>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Therapeutic"
        message={`Are you sure you want to delete "${therapeutic?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isDangerous={true}
        requireDoubleConfirm={isProduction}
        doubleConfirmMessage={`You are about to permanently delete "${therapeutic?.name}" from the PRODUCTION database. This action is irreversible and cannot be undone.`}
      />

      <ReviewChangesDialog
        isOpen={showReviewDialog}
        title="Review Production Changes"
        originalData={originalData}
        modifiedData={formData}
        onConfirm={performSave}
        onCancel={() => setShowReviewDialog(false)}
        entityType="therapeutic"
      />

      <DiffReviewer
        isOpen={showDiffReviewer}
        originalData={formData}
        enrichedData={enrichmentData}
        onApply={handleApplyEnrichment}
        onCancel={() => setShowDiffReviewer(false)}
        title="Review AI Enrichment Suggestions"
      />
    </Box>
  );
};

export default TherapeuticForm;
